import { FC, ReactNode } from "react";
import { formatNumber } from "src/utility";
import PureNumberInput from "@Components/PureNumberInput/PureNumberInput";
import { ConversionInputs } from "src/components";
import { DollarCurrencyIcon, WarningIcon, ExclamationIcon } from "@Icons/MainIcons";
import i18n from "src/i18n";
import { FailureBanner } from "../components/FailureBanner";

type PaymentMethod = "points" | "cash" | "mix";

type Payment = {
  paymentMethod: PaymentMethod;
  points: number;
  cash: number;
};

type MixError = "insufficientPoints" | "exceedsPrice";

type PaymentOption = {
  paymentMethod: PaymentMethod;
  allowed: boolean;
};
type PaymentSummaryContext = {
  selectedPayment: Payment;
  currency: string;
};

type PaymentSlotContext = PaymentSummaryContext & {
  conversionFactor: number;
  maxMixPoints: number;
  mixError: MixError | null;
  updateMix: (points: number) => void;
};

type PaymentMethodConfig = {
  title: ReactNode;
  subTitle: ReactNode;
  footer: ReactNode;
  buttonText: ReactNode;
  renderBody: (ctx: PaymentSlotContext) => ReactNode;
  renderSummaryValue: (ctx: PaymentSummaryContext) => ReactNode;
  renderSummaryNote: (ctx: PaymentSummaryContext) => ReactNode;
};

const INITIAL_PAYMENT_OPTIONS: PaymentOption[] = [
  { paymentMethod: "points", allowed: false },
  { paymentMethod: "mix", allowed: true },
  { paymentMethod: "cash", allowed: true },
];

const RemainingCashBanner: FC<{ cash: number; currency: string }> = ({ cash, currency }) => (
  <div className="bg-background-active/10 text-border-active flex items-center gap-2 rounded-md p-3 text-sm font-semibold">
    <DollarCurrencyIcon className="h-4 w-4 shrink-0" />
    <span>{i18n.t("Hotels.payment.remainingCashToPay", { amount: formatNumber(cash), currency })}</span>
  </div>
);

const MIX_ERROR_KEYS: Record<MixError, string> = {
  insufficientPoints: "Hotels.payment.mixInsufficientPoints",
  exceedsPrice: "Hotels.payment.mixExceedsPrice",
};

const MIX_ERROR_ID = "mix-points-error";

const MixErrorBanner: FC<{ error: MixError; maxPoints: number }> = ({ error, maxPoints }) => (
  <div id={MIX_ERROR_ID} role="alert" className="bg-background-danger text-font-danger flex items-center gap-2 rounded-md p-3 text-sm font-semibold">
    <ExclamationIcon className="h-4 w-4 shrink-0" />
    <span>{i18n.t(MIX_ERROR_KEYS[error], { points: formatNumber(maxPoints) })}</span>
  </div>
);

const resolveSummaryMethod = ({ selectedPayment }: PaymentSummaryContext): PaymentMethod => {
  if (selectedPayment.cash <= 0) return "points";
  if (selectedPayment.points <= 0) return "cash";
  return "mix";
};

const PAYMENT_METHODS: Record<PaymentMethod, PaymentMethodConfig> = {
  points: {
    get title() {
      return i18n.t("Hotels.payment.methodPointsTitle");
    },
    get subTitle() {
      return i18n.t("Hotels.payment.methodPointsSubtitle");
    },
    get footer() {
      return i18n.t("Hotels.payment.methodPointsFooter");
    },
    get buttonText() {
      return i18n.t("Hotels.payment.methodPointsButton");
    },
    renderBody: ({ selectedPayment }) => (
      <div className="text-lg font-semibold">
        {i18n.t("Hotels.payment.pointsValue", { points: formatNumber(selectedPayment.points) })}
      </div>
    ),
    renderSummaryValue: ({ selectedPayment }) =>
      i18n.t("Hotels.payment.pointsValue", { points: formatNumber(selectedPayment.points) }),
    renderSummaryNote: () => i18n.t("Hotels.payment.summaryPointsNote"),
  },
  cash: {
    get title() {
      return i18n.t("Hotels.payment.methodCashTitle");
    },
    get subTitle() {
      return i18n.t("Hotels.payment.methodCashSubtitle");
    },
    get footer() {
      return i18n.t("Hotels.payment.methodConfirmFooter");
    },
    get buttonText() {
      return i18n.t("Hotels.payment.methodConfirmButton");
    },
    renderBody: ({ selectedPayment, currency }) => (
      <div className="text-lg font-semibold">
        {i18n.t("Hotels.payment.cashValue", { amount: formatNumber(selectedPayment.cash), currency })}
      </div>
    ),
    renderSummaryValue: ({ selectedPayment, currency }) =>
      i18n.t("Hotels.payment.cashValue", { amount: formatNumber(selectedPayment.cash), currency }),
    renderSummaryNote: () => i18n.t("Hotels.payment.summaryCashNote"),
  },
  mix: {
    get title() {
      return i18n.t("Hotels.payment.methodMixTitle");
    },
    get subTitle() {
      return i18n.t("Hotels.payment.methodMixSubtitle");
    },
    get footer() {
      return i18n.t("Hotels.payment.methodConfirmFooter");
    },
    get buttonText() {
      return i18n.t("Hotels.payment.methodConfirmButton");
    },
    renderSummaryValue: (ctx) => {
      const method = resolveSummaryMethod(ctx);
      if (method !== "mix") return PAYMENT_METHODS[method].renderSummaryValue(ctx);
      return i18n.t("Hotels.payment.summaryMixValue", {
        amount: formatNumber(ctx.selectedPayment.cash),
        currency: ctx.currency,
        points: formatNumber(ctx.selectedPayment.points),
      });
    },
    renderSummaryNote: (ctx) => {
      const method = resolveSummaryMethod(ctx);
      if (method !== "mix") return PAYMENT_METHODS[method].renderSummaryNote(ctx);
      return i18n.t("Hotels.payment.summaryMixNote");
    },
    renderBody: ({ selectedPayment, currency, conversionFactor, maxMixPoints, mixError, updateMix }) => {
      const redeemedPointsValue = selectedPayment.points / conversionFactor;
      return (
        <div className="flex flex-col gap-3">
          <ConversionInputs
            fromLabel={i18n.t("Hotels.payment.conversionPointsLabel")}
            toLabel={i18n.t("Hotels.payment.conversionCashLabel")}
            fromInput={
              <PureNumberInput
                value={selectedPayment.points}
                min={0}
                status={mixError ? "error" : undefined}
                aria-invalid={!!mixError}
                aria-describedby={mixError ? MIX_ERROR_ID : undefined}
                onChange={(value) => updateMix(Number(value))}
                size="large"
                className="w-full! font-semibold!"
              />
            }
            toInput={
              <PureNumberInput
                value={formatNumber(redeemedPointsValue)}
                disabled
                variant="filled"
                size="large"
                className="w-full! font-semibold!"
              />
            }
          />
          {mixError ? (
            <MixErrorBanner error={mixError} maxPoints={maxMixPoints} />
          ) : (
            <RemainingCashBanner cash={selectedPayment.cash} currency={currency} />
          )}
        </div>
      );
    },
  },
};

type PaymentFlowSuccessContext = {
  navigate: (path: string) => void;
  hotelDetailsKey?: string;
  referenceNumber: string;
  clientSecret?: string;
  returnURL?:string;
  setIsPaymentStep: (value: boolean) => void;
};

export interface FlowContext {
  goToListing: () => void;
  reBook: () => void;
  getFare: () => { oldFare: number; newFare: number };
  isLoading: boolean;
}

export interface BookingFlags {
  isDepartureTimeChanged?: boolean;
  isArrivalTimeChanged?: boolean;
  isSSRDenied?: boolean;
  isPriceChanged?: boolean;
  bookingFailed?: boolean;
}

export interface FailContext extends FlowContext {
  flags: BookingFlags;
}

interface ButtonConfig {
  title: string;
  onClick: (ctx: FlowContext) => void;
  className?: string;
  isRebook?: boolean;
}

interface FlowDefinition {
  value: string;
  keys: Partial<Record<keyof BookingFlags, boolean>>;
  failReason: ReactNode;
  suggestion: ReactNode;
  className?: string;
  icon: ReactNode;
  info?: (ctx: FlowContext) => ReactNode;
  buttons: ButtonConfig[];
}

export interface ResolvedFlow {
  failReason: ReactNode;
  suggestion: ReactNode;
  className?: string;
  icon: ReactNode;
  buttons: Array<{
    title: string;
    onClick: () => void;
    className?: string;
    loading?: boolean;
  }>;
  info?: ReactNode;
}

function createFlow(def: FlowDefinition) {
  return {
    value: def.value,
    keys: def.keys,
    resolve: (ctx: FlowContext): ResolvedFlow => ({
      failReason: def.failReason,
      suggestion: <div>{def.suggestion}</div>,
      className: def.className,
      icon: def.icon,
      info: def.info?.(ctx),
      buttons: def.buttons.map(({ title, onClick, className, isRebook }) => ({
        title,
        className,
        onClick: () => onClick(ctx),
        loading: isRebook ? ctx.isLoading : false,
      })),
    }),
  };
}

const BASE_ICON_CLASS = "h-full w-full rounded-xl border-2 border-background-default p-3";

function getFlows() {
  return [
    createFlow({
      value: "4",
      keys: { bookingFailed: true },
      failReason: <span className="text-font-danger!">{i18n.t("Hotels.payment.bookingFailed")}</span>,
      suggestion: <span className="text-font-subtle! ">{i18n.t("Hotels.payment.bookingFailedSuggestion")}</span>,
      className: "bg-background-danger!",
      icon: <ExclamationIcon className={`${BASE_ICON_CLASS} bg-background-danger! border-none! text-font-danger!`} />,
      buttons: [
        {
          title: i18n.t("Hotels.payment.tryBookingAgain"),
          onClick: ({ reBook }) => reBook(),
          className: "bg-background-default! font-semibold! text-font-danger!",
          isRebook: true,
        },
        {
          title: i18n.t("Hotels.payment.searchNewHotel"),
          onClick: ({ goToListing }) => goToListing(),
          className: "text-font-inverted! font-semibold! bg-background-primary!",
        },
      ],
    }),
    createFlow({
      value: "3",
      keys: { isPriceChanged: true },
      failReason: i18n.t("Hotels.payment.priceChanged"),
      suggestion: i18n.t("Hotels.payment.priceChangedSuggestion"),
      icon: <WarningIcon className={`${BASE_ICON_CLASS} bg-secondary-60! text-font-inverted!`} />,
      info: (ctx) => {
        const { oldFare, newFare } = ctx.getFare();
        return (
          <p className="text-sm text-font-subtle">
            {i18n.t("Hotels.payment.originalFare")} <span className="text-font-danger!">{oldFare}</span> → {i18n.t("Hotels.payment.newFare")} <span>{newFare}</span>
          </p>
        );
      },
      buttons: [
        {
          title: i18n.t("Hotels.payment.confirmPriceAndBook"),
          onClick: ({ reBook }) => reBook(),
          className: "text-font-inverted! bg-background-active! font-semibold!",
          isRebook: true,
        },
        {
          title: i18n.t("Hotels.payment.searchNewHotels"),
          onClick: ({ goToListing }) => goToListing(),
          className: "bg-background-subtle! font-semibold!",
        },
      ],
    }),
  ];
}

export function resolveActiveFlow(flags: BookingFlags, ctx: FlowContext): ResolvedFlow | null {
  for (const flow of getFlows()) {
    const isMatch = (Object.entries(flow.keys) as [keyof BookingFlags, boolean][]).some(
      ([key, expectedValue]) => flags[key] === expectedValue,
    );
    if (isMatch) {
      return flow.resolve(ctx);
    }
  }

  return null;
}

type PaymentFlow = {
  check: (payment: Payment) => boolean;
  execute: (runInitiate: (payment: Payment) => Promise<any>, payment: Payment) => Promise<any>;
  onSuccess: (ctx: PaymentFlowSuccessContext) => void;
  onFail?: (ctx: FailContext) => ReactNode;
};

function persistReferenceNumber(hotelDetailsKey: string | undefined, referenceNumber: string) {
  if (!hotelDetailsKey) return;
  const stored = localStorage.getItem(hotelDetailsKey);
  if (!stored) return;
  localStorage.setItem(hotelDetailsKey, JSON.stringify({ ...JSON.parse(stored), referenceNumber }));
}

export const PAYMENT_FLOWS: PaymentFlow[] = [
  {
    check: (payment) => payment.points === 0 && payment.cash === 0,
    execute: async () => null,
    onSuccess: () => {},
  },
  {
    check: (payment) => payment.cash === 0 && payment.points > 0,
    execute: (runInitiate, payment) => runInitiate(payment),
    onSuccess: ({ navigate, hotelDetailsKey, returnURL, referenceNumber }) => {
      persistReferenceNumber(hotelDetailsKey, referenceNumber);
      if (!returnURL) return null;
      const { pathname, search } = new URL(returnURL);
      return navigate(`${pathname}${search}`);
    },
    onFail: (failCtx) => {
      const flow = resolveActiveFlow(failCtx.flags, failCtx);
      return flow ? <FailureBanner flow={flow} /> : null;
    },
  },
  {
    // cash or mix payment
    check: (payment) => payment.cash > 0,
    execute: (runInitiate, payment) => runInitiate(payment),
    onSuccess: ({ hotelDetailsKey, referenceNumber, setIsPaymentStep }) => {
      persistReferenceNumber(hotelDetailsKey, referenceNumber);
      setIsPaymentStep(true);
    },
    onFail: (failCtx) => {
      const flow = resolveActiveFlow(failCtx.flags, failCtx);
      return flow ? <FailureBanner flow={flow} /> : null;
    },
  },
];

export { PAYMENT_METHODS, INITIAL_PAYMENT_OPTIONS };
export type { PaymentMethod, PaymentMethodConfig, PaymentSlotContext, PaymentSummaryContext, Payment, PaymentOption, MixError };
